import {
  forwardRef,
  useLayoutEffect,
  useRef,
  useState,
  type HTMLAttributes,
  type ReactNode,
  type TableHTMLAttributes,
  type TdHTMLAttributes,
  type ThHTMLAttributes,
} from 'react';

import { cn } from './utils';

export interface TableContainerProps extends HTMLAttributes<HTMLDivElement> {
  /** Fill a constrained flex parent, or grow naturally when the page owns scrolling. */
  fillHeight?: boolean;
}

export const TableContainer = forwardRef<HTMLDivElement, TableContainerProps>(
  function TableContainer({ children, className, fillHeight = false, onScroll, ...props }, ref) {
    const mainScrollRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const topScrollRef = useRef<HTMLDivElement>(null);
    const bottomScrollRef = useRef<HTMLDivElement>(null);
    const [contentWidth, setContentWidth] = useState(0);
    const [containerWidth, setContainerWidth] = useState(0);

    useLayoutEffect(() => {
      const content = contentRef.current;
      const mainScroll = mainScrollRef.current;
      if (!content || !mainScroll) return;

      const updateWidth = (): void => setContentWidth(content.scrollWidth);
      const updateContainerWidth = (): void => setContainerWidth(mainScroll.clientWidth);
      updateWidth();
      updateContainerWidth();
      const observer = new ResizeObserver(() => {
        updateWidth();
        updateContainerWidth();
      });
      observer.observe(content);
      observer.observe(mainScroll);
      return () => observer.disconnect();
    }, []);

    const hasHorizontalOverflow = contentWidth > containerWidth + 1;

    function setMainScrollRef(node: HTMLDivElement | null): void {
      mainScrollRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    }

    function syncHorizontal(source: HTMLDivElement, target: HTMLDivElement | null): void {
      if (target) target.scrollLeft = source.scrollLeft;
    }

    function syncFromTop(): void {
      const source = topScrollRef.current;
      if (!source) return;
      syncHorizontal(source, mainScrollRef.current);
      syncHorizontal(source, bottomScrollRef.current);
    }

    function syncFromBottom(): void {
      const source = bottomScrollRef.current;
      if (!source) return;
      syncHorizontal(source, mainScrollRef.current);
      syncHorizontal(source, topScrollRef.current);
    }

    function syncFromMain(): void {
      const source = mainScrollRef.current;
      if (!source) return;
      syncHorizontal(source, topScrollRef.current);
      syncHorizontal(source, bottomScrollRef.current);
    }

    return (
      <div
        className={cn(
          'data-table-shell w-full overflow-hidden rounded-2xl border border-slate-200 bg-white',
          fillHeight && 'flex min-h-0 flex-col',
          className,
        )}
      >
        {hasHorizontalOverflow ? (
          <div
            ref={topScrollRef}
            aria-hidden="true"
            className="app-scrollbar h-3 shrink-0 overflow-x-auto overflow-y-hidden border-b border-slate-100 bg-slate-50/40"
            onScroll={syncFromTop}
          >
            <div style={{ width: contentWidth, height: 1 }} />
          </div>
        ) : null}
        <div
          {...props}
          ref={setMainScrollRef}
          className={cn(
            'overflow-x-hidden overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch]',
            fillHeight ? 'min-h-0 flex-1' : 'max-h-[min(70vh,720px)]',
          )}
          onScroll={(event) => {
            onScroll?.(event);
            syncFromMain();
          }}
        >
          <div ref={contentRef} className="w-max min-w-full">
            {children}
          </div>
        </div>
        {hasHorizontalOverflow ? (
          <div
            ref={bottomScrollRef}
            aria-label="Desplazamiento horizontal de la tabla"
            className="app-scrollbar h-3 shrink-0 overflow-x-auto overflow-y-hidden border-t border-slate-100 bg-slate-50/40"
            onScroll={syncFromBottom}
          >
            <div style={{ width: contentWidth, height: 1 }} />
          </div>
        ) : null}
      </div>
    );
  },
);

TableContainer.displayName = 'TableContainer';

/**
 * Standard table shell for every operational table in the application.
 * TableContainer remains as a backwards-compatible alias for existing screens.
 */
export const DataTable = TableContainer;
DataTable.displayName = 'DataTable';

export const Table = forwardRef<HTMLTableElement, TableHTMLAttributes<HTMLTableElement>>(
  function Table({ className, ...props }, ref) {
    return (
      <table
        {...props}
        ref={ref}
        className={cn('w-full min-w-[680px] border-collapse text-left text-sm', className)}
      />
    );
  },
);

Table.displayName = 'Table';

export const TableHeader = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(function TableHeader({ className, ...props }, ref) {
  return (
    <thead
      {...props}
      ref={ref}
      className={cn('sticky top-0 z-10 border-b border-slate-200 bg-slate-50/95', className)}
    />
  );
});

TableHeader.displayName = 'TableHeader';

export const TableBody = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(function TableBody({ className, ...props }, ref) {
  return <tbody {...props} ref={ref} className={cn('divide-y divide-slate-100', className)} />;
});

TableBody.displayName = 'TableBody';

export const TableFooter = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(function TableFooter({ className, ...props }, ref) {
  return (
    <tfoot
      {...props}
      ref={ref}
      className={cn('border-t border-slate-200 bg-slate-50/70', className)}
    />
  );
});

TableFooter.displayName = 'TableFooter';

export interface TableRowProps extends HTMLAttributes<HTMLTableRowElement> {
  interactive?: boolean;
  selected?: boolean;
}

export const TableRow = forwardRef<HTMLTableRowElement, TableRowProps>(function TableRow(
  { className, interactive = false, selected = false, ...props },
  ref,
) {
  return (
    <tr
      {...props}
      ref={ref}
      className={cn(
        'bg-white transition-colors',
        interactive && 'cursor-pointer hover:bg-brand-50/75 focus-within:bg-brand-50/75',
        selected && 'bg-brand-50/90',
        className,
      )}
    />
  );
});

TableRow.displayName = 'TableRow';

export interface TableHeadProps extends ThHTMLAttributes<HTMLTableCellElement> {
  align?: 'left' | 'center' | 'right';
}

export const TableHead = forwardRef<HTMLTableCellElement, TableHeadProps>(function TableHead(
  { align = 'left', className, scope = 'col', ...props },
  ref,
) {
  return (
    <th
      {...props}
      ref={ref}
      scope={scope}
      className={cn(
        'h-11 whitespace-nowrap px-4 text-xs font-semibold uppercase tracking-wide text-slate-500',
        align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left',
        className,
      )}
    />
  );
});

TableHead.displayName = 'TableHead';

export interface TableCellProps extends TdHTMLAttributes<HTMLTableCellElement> {
  align?: 'left' | 'center' | 'right';
  muted?: boolean;
}

export const TableCell = forwardRef<HTMLTableCellElement, TableCellProps>(function TableCell(
  { align = 'left', className, muted = false, ...props },
  ref,
) {
  return (
    <td
      {...props}
      ref={ref}
      className={cn(
        'px-4 py-3.5 align-middle text-sm text-slate-700',
        align === 'right'
          ? 'text-right tabular-nums'
          : align === 'center'
            ? 'text-center'
            : 'text-left',
        muted && 'text-slate-500',
        className,
      )}
    />
  );
});

TableCell.displayName = 'TableCell';

export interface TableEmptyStateProps {
  colSpan: number;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
}

/** Place inside `TableBody` whenever a filtered collection has no rows. */
export function TableEmptyState({ action, colSpan, description, title }: TableEmptyStateProps) {
  return (
    <tr>
      <td className="px-6 py-12 text-center" colSpan={colSpan}>
        <p className="text-sm font-semibold text-slate-700">{title}</p>
        {description ? (
          <p className="mx-auto mt-1.5 max-w-md text-sm leading-6 text-slate-500">{description}</p>
        ) : null}
        {action ? <div className="mt-4">{action}</div> : null}
      </td>
    </tr>
  );
}

export interface DataListProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

/**
 * A mobile-first alternative for dense tables. Use it alongside a desktop
 * TableContainer (`md:hidden` / `hidden md:block`) when each record needs labels.
 */
export const DataList = forwardRef<HTMLDivElement, DataListProps>(function DataList(
  { className, ...props },
  ref,
) {
  return (
    <div
      {...props}
      ref={ref}
      className={cn(
        'divide-y divide-slate-100 overflow-hidden rounded-2xl border border-slate-200 bg-white',
        className,
      )}
    />
  );
});

DataList.displayName = 'DataList';

export const DataListItem = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function DataListItem({ className, ...props }, ref) {
    return <div {...props} ref={ref} className={cn('px-4 py-4', className)} />;
  },
);

DataListItem.displayName = 'DataListItem';

export interface DataListRowProps extends HTMLAttributes<HTMLDivElement> {
  label: ReactNode;
}

export const DataListRow = forwardRef<HTMLDivElement, DataListRowProps>(function DataListRow(
  { children, className, label, ...props },
  ref,
) {
  return (
    <div
      {...props}
      ref={ref}
      className={cn('flex items-start justify-between gap-5 py-1.5 text-sm', className)}
    >
      <span className="shrink-0 text-slate-500">{label}</span>
      <span className="min-w-0 text-right font-medium text-slate-700">{children}</span>
    </div>
  );
});

DataListRow.displayName = 'DataListRow';
